<template>
  <button
    class="portfolio-card"
    data-testid="portfolio-card"
    :disabled="isPortfolioDisabled"
    @click="handleClick"
  >
    <ProgressiveImage
      :placeholder="portfolio.image"
      :image="portfolio.image"
      :class="classes.progressiveImage"
    />
    <div :class="classes.card">
      <div :class="classes.content">
        <div class="portfolio-card__chips">
          <ChipContext
            v-if="portfolio.showManagementIcon"
            class="portfolio-card__chip-context"
            label=""
            icon="G0010"
            context="brand"
          />
          <ChipStatus
            v-for="(label, index) in portfolio.status"
            :key="index"
            data-testid="portfolio-status"
            class="portfolio-card__chip-status"
            :label="label"
            status="information"
          />
        </div>
        <div>
          <Heading
            class="portfolio-card__title"
            :title="portfolio.name"
            :level="5"
          >
            {{ portfolio.name }}
          </Heading>
          <Money>
            <BaseText size="p5" :color="NebraskaColors.textOverImage">
              {{ portfolio.value.formatted }}
            </BaseText>
          </Money>
        </div>
      </div>
      <div
        v-if="portfolio.message"
        class="portfolio-card__message"
        data-testid="portfolio-message"
      >
        <BaseText size="p6" :color="NebraskaColors.textOverImage">
          {{ portfolio.message }}
        </BaseText>
      </div>
    </div>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  Heading,
  ChipStatus,
  ChipContext
} from '@warrenbrasil/nebraska-web';
import { ProgressiveImage } from '@/modules/common/components/ProgressiveImage';
import { Money } from '@/modules/common/components/Money';

import { IPortfolioList } from '@/types/models/Portfolio';

interface Emits {
  (event: 'click', id: string): void;
}
const emits = defineEmits<Emits>();

interface Props {
  portfolio: IPortfolioList;
}
const props = defineProps<Props>();

const isPortfolioDisabled = computed(() => {
  return !props.portfolio.enabled || false;
});

const classes = computed(() => {
  return {
    progressiveImage: {
      'portfolio-card__background': true,
      'portfolio-card__background--inactive':
        props.portfolio.isInactive || isPortfolioDisabled.value
    },
    card: {
      'portfolio-card__card': true,
      'portfolio-card__card--disabled': isPortfolioDisabled.value
    },
    content: {
      'portfolio-card__content': true,
      'portfolio-card__content--has-message': props.portfolio.message
    }
  };
});

function handleClick() {
  if (isPortfolioDisabled.value) {
    return;
  }

  emits('click', props.portfolio.id);
}
</script>

<style lang="less" scoped>
.portfolio-card {
  position: relative;
  padding: 0;
  border-radius: @size-radius-x400;
  height: 240px;
  min-width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;

  &:hover :deep(img) {
    filter: blur(3px);
  }

  &__background {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &--inactive {
      filter: grayscale(100%);
    }

    &::before {
      content: '';
      position: absolute;
      z-index: 2;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(51, 49, 49, 0) 13%,
        rgba(51, 49, 49, 0.65) 82.75%
      );
    }
  }

  &__card {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;

    &--disabled {
      cursor: not-allowed !important;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between;
    padding: @size-spacing-x600 @size-spacing-x500 @size-spacing-x500;

    &--has-message {
      padding-bottom: @size-spacing-x400;
    }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: @size-spacing-x300;
  }

  &__chip-context {
    background: @status-brand-background;
    padding: @size-spacing-x150 @size-spacing-x150;
    color: @status-brand;

    .chip-context--brand {
      height: 22px;
    }

    :deep(.chip--icon) {
      margin-right: 0;
    }

    :deep(svg) {
      width: 14px !important;
      height: 14px !important;
    }
  }

  &__chip-status {
    height: 22px;
  }

  &__title {
    color: @text-over-image;
    margin-bottom: @size-spacing-x150;
  }

  &__message {
    width: 100%;
    padding: @size-spacing-x350 @size-spacing-x500;
    background-color: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(16px);
    border-radius: 0 0 @size-radius-x400 @size-radius-x400;
  }
}
</style>
